import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import axios from 'axios'
import reduceTracks from 'api/reducers/reduceTracks'
import reducePlaylists from 'api/reducers/reducePlaylists'
import reduceWaveform from './reducers/reduceWaveform'

axios.defaults.withCredentials = true

const endpoints = {
  session: `${process.env.REACT_APP_API_ENDPOINT}/session`,
  track: `${process.env.REACT_APP_API_ENDPOINT}/tracks`,
  image: `${process.env.REACT_APP_API_ENDPOINT}/images`,
  users: `${process.env.REACT_APP_API_ENDPOINT}/users`,
}

function postTracks(formData, onUploadProgress) {
  const controller = new AbortController()
  return [
    axios.post(endpoints.track, formData, {
      onUploadProgress,
      signal: controller.signal,
    }),
    controller,
  ]
}

function replaceTrack(id, formData, onUploadProgress) {
  const controller = new AbortController()
  return [
    axios.post(
      `${process.env.REACT_APP_API_ENDPOINT}/tracks/${id}/replace`,
      formData,
      {
        onUploadProgress,
        signal: controller.signal,
      },
    ),
    controller,
  ]
}

async function postImage(folder, formData, onUploadProgress) {
  return axios.post(`${endpoints.image}/${folder}`, formData, {
    onUploadProgress,
  })
}

async function getTrack(trackId) {
  return axios.get(`${endpoints.track}/${trackId}`)
}

async function playTrack(trackId) {
  return axios
    .get(`${endpoints.track}/${trackId}/play`)
    .then((res) => res?.data?.url)
}

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_ENDPOINT,
  }),
  endpoints: (builder) => ({
    session: builder.query({
      query: () => `session`,
      providesTags: (result) => [{ type: 'User', id: 1 }],
    }),
    postLogin: builder.mutation({
      query: ({ email, password }) => ({
        url: `session`,
        method: 'POST',
        body: {
          email,
          password,
        },
      }),
    }),
    postChallenge: builder.mutation({
      query: () => ({
        url: `challenge`,
        method: 'POST',
      }),
    }),
    putChallenge: builder.mutation({
      query: ({ code }) => ({
        url: `challenge`,
        method: 'PUT',
        body: {
          code,
        },
      }),
      invalidatesTags: (result) =>
        result?.id ? [{ type: 'User', id: 1 }] : null,
    }),
    deleteSession: builder.mutation({
      query: () => ({
        url: 'session',
        method: 'delete',
      }),
    }),
    postResetPassword: builder.mutation({
      query: (email) => ({
        url: 'passwordReset',
        method: 'POST',
        body: {
          email,
        },
      }),
    }),
    putResetPassword: builder.mutation({
      query: ({ id, password }) => ({
        url: 'passwordReset',
        method: 'PUT',
        body: {
          id,
          password,
        },
      }),
      invalidatesTags: (result) =>
        result?.id ? [{ type: 'User', id: 1 }] : null,
    }),
    postUser: builder.mutation({
      query: ({ name, email, password }) => ({
        url: 'users',
        method: 'POST',
        body: {
          name,
          email,
          password,
        },
      }),
      invalidatesTags: (result) =>
        result?.id ? [{ type: 'User', id: 1 }] : null,
    }),
    updateUser: builder.mutation({
      query: (body) => ({
        url: 'users',
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result) =>
        result?.id ? [{ type: 'User', id: 1 }] : null,
    }),
    putUserPreference: builder.mutation({
      query: (body) => ({
        url: 'users/preferences',
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result) =>
        result?.id ? [{ type: 'User', id: 1 }] : null,
    }),
    tracks: builder.query({
      query: () => `tracks`,
      transformResponse: (response, meta, arg) => {
        return reduceTracks(response)
      },
      providesTags: (result) => {
        return result?.data
          ? [
              ...result?.data?.map(({ id }) => ({ type: 'Tracks', id })),
              { type: 'Tracks', id: 'LIST' },
            ]
          : [{ type: 'Tracks', id: 'LIST' }]
      },
    }),
    images: builder.query({
      query: (folder) => `images/${folder}`,
      providesTags: (result) =>
        result?.data
          ? [
              ...result?.data?.map(({ url }) => ({ type: 'Images', url })),
              { type: 'Images', id: 'LIST' },
            ]
          : [{ type: 'Images', id: 'LIST' }],
    }),
    waveform: builder.query({
      query: (url) => ({
        url,
        // This is the same as passing 'text'
        responseHandler: async (response) => {
          return await reduceWaveform(response)
        },
      }),
      providesTags: (result, error, url) => {
        return [{ type: 'Track-Waveform', url }]
      },
      keepUnusedDataFor: 999999999999999999,
    }),
    playTrack: builder.query({
      query: (trackId) => `/tracks/${trackId}/play`,

      keepUnusedDataFor: 3600,
    }),
    addTrack: builder.mutation({
      query: (track) => ({
        url: '/tracks',
        method: 'POST',
        body: track,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Tracks', id }],
    }),
    updateTrack: builder.mutation({
      query: ({ id, values }) => ({
        url: `/tracks/${id}`,
        method: 'PUT',
        body: values,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Tracks', id },
        { type: 'Playlists', id: 'LIST' },
      ],
    }),
    updateTracks: builder.mutation({
      query: ({ ids, values }) => ({
        url: `/tracks`,
        method: 'PUT',
        body: { ids, values },
      }),
      invalidatesTags: (result, error, { ids }) => {
        return ids.map((id) => ({ type: 'Tracks', id }))
      },
    }),
    deleteTracks: builder.mutation({
      query: (ids) => ({
        url: `/tracks`,
        body: { ids },
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Tracks', id },
        { type: 'Playlists', id: 'LIST' },
      ],
    }),
    playlists: builder.query({
      query: () => `playlists`,
      transformResponse: (response, meta, arg) => {
        return reducePlaylists(response)
      },
      providesTags: (result) => [{ type: 'Playlists', id: 'LIST' }],
    }),
    updatePlaylists: builder.mutation({
      query: (data) => ({
        url: `/playlists`,
        method: 'PUT',
        body: {
          data,
        },
      }),
      invalidatesTags: (result, error) => [{ type: 'Playlists' }],
    }),
    player: builder.query({
      query: (playlistId) => `/playlists/${playlistId}/player`,
    }),
    updatePlayer: builder.mutation({
      query: (playlistId) => ({
        url: `/playlists/${playlistId}/player`,
        method: 'PUT',
        body: {},
      }),
      invalidatesTags: (result, error) => [{ type: 'Playlists' }],
    }),
    portfolios: builder.query({
      query: ({ page = 1, search = '' }) =>
        `portfolios?page=${page}&search=${search}`,

      providesTags: (result) =>
        result?.data
          ? [
              ...result?.data?.map(({ id }) => ({ type: 'Portfolios', id })),
              { type: 'Portfolios', id: 'LIST' },
            ]
          : [{ type: 'Portfolios', id: 'LIST' }],
    }),
    portfolio: builder.query({
      query: (id) => `/portfolios/${id}`,
      providesTags: (result, error, id) => {
        return [{ type: 'Portfolio', id }]
      },
    }),
    createPortfolio: builder.mutation({
      query: (body) => ({
        url: '/portfolios',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Portfolios', id: 'LIST' },
      ],
    }),
    updatePortfolio: builder.mutation({
      query: ({ id, values }) => ({
        url: `/portfolios/${id}`,
        method: 'PUT',
        body: values,
      }),
      invalidatesTags: (result, error, { id }) => {
        return [
          { type: 'Portfolio', id },
          { type: 'Portfolios', id: 'LIST' },
        ]
      },
    }),
    deletePortfolios: builder.mutation({
      query: (ids) => ({
        url: `/portfolios`,
        body: { ids },
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Portfolios', id }],
    }),
    portfolioSlugCheck: builder.mutation({
      query: ({ id, slug }) => ({
        url: `/portfolios/${id}/slug-check/${slug}`,
        method: 'GET',
      }),
    }),
    duplicatePortfolio: builder.mutation({
      query: ({ id, slug, name }) => ({
        url: `/portfolios/${id}/duplicate`,
        method: 'POST',
        body: {
          slug,
          name,
        },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Portfolios', id: 'LIST' },
      ],
    }),
    setUserPreference: builder.mutation({
      query: (body) => ({
        url: `/user/preferences`,
        method: 'PUT',
        body,
      }),
    }),
    subscription: builder.query({
      query: () => `/payments`,
      providesTags: (result, error, id) => {
        return [{ type: 'Subscription', id: 1 }]
      },
    }),
    createPayment: builder.mutation({
      query: (type) => ({
        url: `/payments`,
        method: 'POST',
        body: {
          type,
        },
      }),
    }),
    managePayment: builder.mutation({
      query: () => ({
        url: `/payments`,
        method: 'PUT',
      }),
    }),
    playerPreview: builder.mutation({
      query: ({ data, styles }) => ({
        url: '/player/preview',
        body: { data, styles },
        method: 'POST',
      }),
    }),
  }),
})

export { postTracks, playTrack, getTrack, postImage, replaceTrack }

export const {
  useLazySessionQuery,
  useSessionQuery,
  usePostLoginMutation,
  usePostUserMutation,
  useTracksQuery,
  usePlayTrackQuery,
  usePlaylistsQuery,
  useSubscriptionQuery,
  useImagesQuery,
  usePlayerQuery,
  useWaveformQuery,
  usePortfolioQuery,
  usePortfoliosQuery,
  useAddTrackMutation,
  useUpdateTrackMutation,
  useUpdateTracksMutation,
  useDeleteTracksMutation,
  useUpdatePlaylistsMutation,
  useUpdatePlayerMutation,
  useCreatePortfolioMutation,
  useUpdatePortfolioMutation,
  usePutUserPreferenceMutation,
  usePostChallengeMutation,
  usePutChallengeMutation,
  useDeleteSessionMutation,
  usePutResetPasswordMutation,
  usePostResetPasswordMutation,
  useDeletePortfoliosMutation,
  useCreatePaymentMutation,
  useManagePaymentMutation,
  useUpdateUserMutation,
  usePlayerPreviewMutation,
  usePortfolioSlugCheckMutation,
  useDuplicatePortfolioMutation,
} = apiSlice
